import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock, PenLine, Play } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import { ChapterVideos, VideoPlayerModal } from "@/components/VideoLearning";
import { getChapters, getSubjects } from "@/data/curriculum";
import { inputClass } from "@/components/AuthLayout";
import { apiFetch, getToken } from "@/api";
import AcademicContextBar from "@/components/AcademicContextBar";

/* Floating circular 4-segment selection indicator — visualizes the EXISTING
   board/klass/subject/chapter state; revolves only while videos load. */
const RING_R = 30;
const RING_C = 2 * Math.PI * RING_R;
const RING_SEG = (RING_C - 4 * 10) / 4;

const SelectionRing = ({ flags, spinning }) => (
  <div data-testid="learn-selection-ring" className="relative h-[84px] w-[84px]">
    <div className={`absolute inset-0 ${spinning ? "animate-ring-spin" : ""}`}>
      <svg width="84" height="84" viewBox="0 0 84 84" className="h-full w-full">
        {flags.map((on, i) => (
          <circle
            key={i}
            data-testid={`ring-segment-${i}`}
            cx="42"
            cy="42"
            r={RING_R}
            fill="none"
            stroke={on ? "#16A34A" : "#DCE5DD"}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${RING_SEG} ${RING_C - RING_SEG}`}
            transform={`rotate(${-90 + i * 90} 42 42)`}
            style={{ transition: "stroke 300ms ease" }}
          />
        ))}
      </svg>
    </div>
    <span
      data-testid="ring-play-btn"
      className="absolute inset-0 m-auto flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#22C55E] to-[#16A34A] shadow-[0_10px_20px_-8px_rgba(22,163,74,0.6)]"
    >
      <Play className="h-4 w-4 fill-current text-white" />
    </span>
  </div>
);

const LearnVideos = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [klass, setKlass] = useState(null);
  const [subject, setSubject] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topic, setTopic] = useState(null); // null = whole chapter
  const [customTopic, setCustomTopic] = useState("");
  const [search, setSearch] = useState(null);
  const [searchStatus, setSearchStatus] = useState(null);
  const [watching, setWatching] = useState(null);
  const [me, setMe] = useState(null);

  // Load plan/usage so the remaining-searches note is live; prefill profile
  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        // Board/Class from the global profile; subject is chosen per visit
        if (u.board) setBoard(u.board === "CBSE" ? "CBSE" : "CISCE");
        if (u.klass) setKlass(String(u.klass));
      })
      .catch(() => {});
  }, []);

  // Chapter picked → load its core topics (AI-generated once, cached globally)
  useEffect(() => {
    setTopic(null);
    setCustomTopic("");
    setTopics([]);
    if (!(board && klass && subject && chapter) || !getToken()) return;
    let cancelled = false;
    setTopicsLoading(true);
    const params = new URLSearchParams({ board, class: String(klass), subject, chapter });
    apiFetch(`/chapter-topics?${params.toString()}`)
      .then((d) => !cancelled && setTopics(d.topics || []))
      .catch(() => {})
      .finally(() => !cancelled && setTopicsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [board, klass, subject, chapter]);

  const handleStatus = (s) => {
    setSearchStatus(s);
    // refresh remaining count after a completed search
    if (s === "ok" && getToken()) apiFetch("/me").then(setMe).catch(() => {});
  };

  const subjectOptions = board && klass ? getSubjects(board, klass) : [];
  const chapterOptions =
    board && klass && subject ? getChapters(board, klass, subject) : [];
  const ready = Boolean(board && klass && subject && chapter);
  const videoLocked = Boolean(me) && me.videoSearchLimit === 0;
  const boardLabel = (v) => (v === "CISCE" ? "ICSE" : v);

  const pill = (active) =>
    `rounded-full border-2 px-4 py-2.5 text-xs font-bold transition-[border-color,background-color] duration-200 ${
      active
        ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
        : "border-[#17201A]/10 bg-[#F8FAF7] text-[#17201A]/60 hover:border-[#16A34A]/40"
    }`;

  // "Change Data" — new global Board/Class; refresh lists, drop stale selections
  const handleContextChange = (newBoard, newKlass) => {
    setBoard(newBoard === "CBSE" ? "CBSE" : "CISCE");
    setKlass(String(newKlass));
    setSubject(null);
    setChapter(null);
    setSearch(null);
    setSearchStatus(null);
  };

  const pickSubject = (s) => {
    setSubject(s);
    setChapter(null);
  };

  return (
    <div
      data-testid="learn-videos-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative z-40 border-b border-[#E3ECE6] bg-[#F8FAF7]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
          <Link
            to="/"
            data-testid="logo-home-link"
            className="font-display text-2xl font-extrabold"
          >
            <img src="/logo.png" alt="Abhyas" className="h-10 w-auto" />
          </Link>
          <UserAvatar />
        </div>
      </header>

      <main className="relative mx-auto max-w-7xl px-6 pb-20 pt-8">
        {/* Floating selection ring in a dashed box (replaces the green band) */}
        <div
          data-testid="learn-hero-ring-box"
          className="rounded-[2rem] border-2 border-dashed border-[#16A34A]/30 bg-white px-7 py-10 text-center shadow-[0_28px_56px_-20px_rgba(22,163,74,0.35)] sm:py-12"
        >
          <div className="flex justify-center">
            <SelectionRing
              flags={[Boolean(board), Boolean(klass), Boolean(subject), Boolean(chapter)]}
              spinning={searchStatus === "loading"}
            />
          </div>
          <h1
            data-testid="learn-ready-title"
            className="mt-5 font-display text-2xl font-extrabold sm:text-3xl"
          >
            Ready to learn?
          </h1>
          <p className="mt-2 text-sm font-medium text-[#647067]">
            Pick your board, class, subject and chapter below to start
            watching.
          </p>
        </div>

        {/* Selection card */}
        <div
          data-testid="learn-selection-card"
          className="mt-6 rounded-[2rem] border border-[#E3ECE6] bg-white p-7 shadow-sm sm:p-9"
        >
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <AcademicContextBar
              board={board === "CISCE" ? "ICSE/ISC" : board}
              klass={klass}
              onChanged={handleContextChange}
              testId="learn-context"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-bold">Subject</label>
              <div className="flex flex-wrap gap-2">
                {subjectOptions.length === 0 && (
                  <p
                    data-testid="learn-subject-hint"
                    className="text-xs font-semibold text-[#647067]"
                  >
                    Select board & class first
                  </p>
                )}
                {subjectOptions.map((s) => (
                  <button
                    key={s}
                    data-testid={`learn-subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => pickSubject(s)}
                    className={pill(subject === s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1.5 block text-sm font-bold">Chapter</label>
              <select
                data-testid="learn-chapter-select"
                value={chapter || ""}
                onChange={(e) => setChapter(e.target.value)}
                disabled={chapterOptions.length === 0}
                className={`${inputClass} appearance-none ${
                  chapterOptions.length === 0
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer"
                }`}
              >
                {chapterOptions.length === 0 && (
                  <option value="">Select subject first</option>
                )}
                {chapterOptions.length > 0 && (
                  <option value="">Select chapter</option>
                )}
                {chapterOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Topic step — chapter's core concepts (optional; default = whole chapter) */}
          {chapter && (
            <div data-testid="learn-topic-step" className="mt-6">
              <label className="mb-1.5 block text-sm font-bold">
                Topic <span className="font-semibold text-[#94A39B]">(optional — defaults to the whole chapter)</span>
              </label>
              {topicsLoading ? (
                <p data-testid="learn-topics-loading" className="inline-flex items-center gap-2 text-xs font-bold text-[#647067]">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[#16A34A]" />
                  Finding topics…
                </p>
              ) : (
                <div data-testid="learn-topic-chips" className="flex flex-wrap gap-2">
                  <button
                    data-testid="learn-topic-whole"
                    onClick={() => { setTopic(null); setCustomTopic(""); }}
                    className={pill(!topic && !customTopic)}
                  >
                    Whole chapter
                  </button>
                  {topics.map((t) => (
                    <button
                      key={t}
                      data-testid={`learn-topic-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => { setTopic(t); setCustomTopic(""); }}
                      className={pill(topic === t && !customTopic)}
                    >
                      {t}
                    </button>
                  ))}
                  <input
                    data-testid="learn-topic-custom"
                    value={customTopic}
                    onChange={(e) => { setCustomTopic(e.target.value); if (e.target.value) setTopic(null); }}
                    placeholder="Or type a topic…"
                    className="w-44 rounded-full border-2 border-[#17201A]/10 bg-[#F8FAF7] px-4 py-2 text-xs font-bold outline-none transition-colors duration-200 focus:border-[#16A34A]"
                  />
                </div>
              )}
            </div>
          )}

          <button
            data-testid="watch-learn-btn"
            data-magnetic="true"
            onClick={() => {
              if (videoLocked) {
                navigate("/pricing");
                return;
              }
              if (ready) setSearch({ board, klass, subject, chapter, topic: (customTopic.trim() || topic || null) });
            }}
            disabled={!videoLocked && !ready}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-[0_16px_32px_-12px_rgba(22,163,74,0.5)] transition-[background-color,transform,opacity] duration-200 hover:bg-[#15803D] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-12"
          >
            {videoLocked ? (
              <Lock className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4 fill-current" />
            )}
            Watch &amp; Learn
          </button>
          {me &&
            !videoLocked && (
              <p
                data-testid="video-usage-note"
                className="mt-3 text-xs font-semibold text-[#647067]"
              >
                {Math.max(
                  (me.videoSearchLimit ?? 0) - (me.videoSearchesToday ?? 0),
                  0
                )}{" "}
                of {me.videoSearchLimit} video searches left today
              </p>
            )}
        </div>

        {/* Results */}
        {search && (
          <div className="mt-10">
            <h2
              data-testid="learn-results-title"
              className="font-display text-xl font-extrabold sm:text-2xl"
            >
              Videos for{" "}
              <span className="italic text-[#16A34A]">{search.topic || search.chapter}</span>
            </h2>
            <p
              data-testid="learn-results-meta"
              className="mt-1 text-xs font-bold uppercase tracking-widest text-[#647067]"
            >
              {boardLabel(search.board)} · Class {search.klass} ·{" "}
              {search.subject}
              {search.topic ? ` · ${search.chapter}` : ""}
            </p>
            <div className="mt-5">
              <ChapterVideos
                board={search.board}
                klass={search.klass}
                subject={search.subject}
                chapter={search.chapter}
                topic={search.topic}
                layout="rich"
                prefix="learn"
                selection={{
                  board: boardLabel(search.board),
                  klass: search.klass,
                  subject: search.subject,
                  chapter: search.chapter,
                }}
                onWatch={setWatching}
                hideLoading
                onStatusChange={handleStatus}
              />
            </div>
            {/* Learning → practice bridge: normal 5-question practice on this topic */}
            {searchStatus === "ok" && (
              <div
                data-testid="learn-practice-bridge"
                className="animate-fade-up mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-[#E3ECE6] bg-white p-6 sm:px-8"
              >
                <div>
                  <p className="font-display text-lg font-extrabold">
                    Understood it? Prove it.
                  </p>
                  <p className="mt-0.5 text-xs font-semibold text-[#647067]">
                    5 practice questions on {search.topic || search.chapter} — about 10 minutes.
                  </p>
                </div>
                <button
                  data-testid="practice-this-topic-btn"
                  onClick={() =>
                    navigate("/practice-setup", {
                      state: {
                        quickStart: {
                          board: search.board,
                          klass: String(search.klass),
                          subject: search.subject,
                          chapter: search.chapter,
                          topic: search.topic || undefined,
                          difficulty: "Medium",
                        },
                      },
                    })
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
                >
                  <PenLine className="h-4 w-4" />
                  Practice this {search.topic ? "topic" : "chapter"} →
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {watching && (
        <VideoPlayerModal video={watching} onClose={() => setWatching(null)} />
      )}
    </div>
  );
};

export default LearnVideos;
